import * as React from 'react';
import type { WrappedFieldProps } from 'redux-form';

import { roundUnitValue } from '@acceligentllc/shared/utils/unit';

import type { CompoundUnitEnum, PlainUnitEnum } from '@acceligentllc/shared/enums/quantityUnit';
import { CompoundUnitValueLimits } from '@acceligentllc/shared/enums/quantityUnit';

import { CompoundInput, type CompoundInputValue } from '@acceligentllc/storybook';

import { SIZE_MAP, type Sizes } from 'af-fields/Input';

import type { OwnProps as TooltipProps } from 'af-components/Tooltip';

interface OwnProps {
	unit: CompoundUnitEnum;
	label?: string;
	disabled?: boolean;
	inputSize?: Sizes;
	tooltipMessage?: TooltipProps['message'];
	disableErrors?: boolean;
	hideErrorText?: boolean;
	forceShowError?: boolean;
	maxDigits?: Nullable<number>;
	onValueChange?: (elementUnit: string, value: number) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

type Props = OwnProps & WrappedFieldProps;

const normalizeElementValue = (
	rawValue: string,
	min: number,
	max: number,
	maxLength?: Nullable<number>
): number | string => {
	if (rawValue === '') {
		return '';
	}
	if (maxLength && rawValue.length > maxLength) {
		return rawValue.slice(0, maxLength);
	}

	const numericValue = Number(rawValue);
	if (numericValue < min) {
		return min;
	}
	if (numericValue > max) {
		return max;
	}
	return numericValue;
};

const CompoundInputField: React.FC<Props> = (props) => {
	const {
		input,
		meta: { touched, error, warning },
		unit,
		label,
		disabled,
		inputSize,
		tooltipMessage,
		maxDigits,
		disableErrors,
		hideErrorText,
		forceShowError,
		onValueChange,
		onFocus,
		onBlur,
		onKeyDown,
	} = props;

	const [firstUnit, secondUnit] = React.useMemo(() => Object.keys(CompoundUnitValueLimits[unit]), [unit]);

	// redux-form hands back '' for an untouched field, so guard the object access
	const value: CompoundInputValue = React.useMemo(() => input.value || {}, [input.value]);

	const handleElementChange = React.useCallback((elementUnit: string, rawValue: string) => {
		const limits = CompoundUnitValueLimits[unit][elementUnit];
		const normalizedValue = normalizeElementValue(rawValue, limits.min, limits.max, maxDigits);

		input.onChange({ ...value, [elementUnit]: normalizedValue });
		onValueChange?.(elementUnit, roundUnitValue(+normalizedValue, elementUnit as PlainUnitEnum));
	}, [unit, maxDigits, value, input, onValueChange]);

	const handleElementBlur = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
		// pass the object explicitly — see note below
		input.onBlur(value);
		onBlur?.(event);
	}, [input, value, onBlur]);

	const hasError = !!(touched && !disableErrors && error);
	const allowErrorMessage = !!(!disableErrors && !hideErrorText && (touched || forceShowError)) && !!error;
	const allowWarningMessage = !!(!disableErrors && !hideErrorText && (touched || forceShowError)) && !!warning;

	return (
		<CompoundInput
			disabled={disabled}
			error={allowErrorMessage ? error : undefined}
			firstUnit={firstUnit}
			firstUnitMax={CompoundUnitValueLimits[unit][firstUnit].max}
			firstUnitMin={CompoundUnitValueLimits[unit][firstUnit].min}
			hasError={hasError}
			label={label}
			onElementBlur={handleElementBlur}
			onElementChange={handleElementChange}
			onElementFocus={onFocus}
			onElementKeyDown={onKeyDown}
			secondUnit={secondUnit}
			secondUnitMax={CompoundUnitValueLimits[unit][secondUnit].max}
			secondUnitMin={CompoundUnitValueLimits[unit][secondUnit].min}
			size={inputSize ? SIZE_MAP[inputSize] : 'regular'}
			tooltip={tooltipMessage}
			value={value}
			warning={allowWarningMessage ? warning : undefined}
		/>
	);
};

export default React.memo(CompoundInputField);
